import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserProfileByUsername } from '../../services/api';
import UserProfile from './UserProfile';
import { motion } from 'framer-motion';
import { ArrowLeft, AlertTriangle as AlertTriangleIcon, UserX } from 'lucide-react';
import styles from './UserProfilePage.module.scss';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
// Standardize on lucide-react

const UserProfilePage = () => {
  const { t } = useTranslation('profile');
  const { username } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const navigate = useNavigate();

  // Animaciones del Design System
  const fadeInLuxury = {
    hidden: {
      opacity: 0,
      y: 30,
      filter: 'blur(10px)'
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 0.8,
        ease: [0.23, 1, 0.32, 1]
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const staggerItem = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.23, 1, 0.32, 1]
      }
    }
  };

  useEffect(() => {
    document.title = username ? `Perfil de ${username} | FOTH` : 'Perfil de Usuario | FOTH';

    const fetchUserProfile = async () => {
      if (!username) {
        setError(t('view.error_not_found'));
        setLoading(false);
        return;
      }

      try {
        const response = await getUserProfileByUsername(username);
        setProfileData(response.data);

        if (response.data && response.data.full_name) {
          document.title = `${response.data.full_name} | FOTH`;
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setError(t('view.error_not_found'));
        } else {
          setError(t('view.error_generic'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();

    return () => {
      document.title = 'FOTH';
    };
  }, [username, t]);

  const handleGoBack = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className={styles.profilePage}>
        <div className={styles.profileContainer}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className={styles.loadingContainer}
          >
            <motion.div
              className={styles.loadingSpinner}
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {t('view.loading')}
            </motion.p>
          </motion.div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.profilePage}>
        <div className={styles.profileContainer}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInLuxury}
            className={styles.profileCard}
          >
            <motion.div
              className={styles.profileBody}
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              <motion.div
                className={clsx(styles.alert, styles.alertDanger)}
                variants={staggerItem}
              >
                <AlertTriangleIcon style={{ marginRight: '10px' }} size={20} />
                {error}
              </motion.div>

              <motion.button
                className={styles.btnPrimary}
                onClick={handleGoBack}
                variants={staggerItem}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ArrowLeft style={{ marginRight: '8px' }} size={16} />
                {t('view.back_home')}
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className={styles.profilePage}>
        <div className={styles.profileContainer}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInLuxury}
            className={styles.profileCard}
          >
            <motion.div
              className={styles.profileBody}
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              <motion.div
                className={clsx(styles.alert, styles.alertWarning)}
                variants={staggerItem}
              >
                <UserX style={{ marginRight: '10px' }} size={20} />
                {t('view.error_not_found')}
              </motion.div>

              <motion.button
                className={styles.btnPrimary}
                onClick={handleGoBack}
                variants={staggerItem}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ArrowLeft style={{ marginRight: '8px' }} size={16} />
                {t('view.back_home')}
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  return <UserProfile userData={profileData} />;
};

export default UserProfilePage;